import { supabase } from '@/integrations/supabase/client';

export type AppRole = 'fan' | 'player' | 'coach' | 'club_owner' | 'scout' | 'scouter' | 'referee' | 'medical_staff' | 'venue_owner' | 'tournament_organizer' | 'team_manager' | 'admin' | 'super_admin' | 'club_admin' | 'club_finance' | 'club_manager' | 'parent';

export const roleUpgradeFlow: Record<string, 'auto_approved' | 'admin_approval'> = {
  'fan_to_player': 'auto_approved',
  'player_to_coach': 'admin_approval',
  'player_to_club_owner': 'admin_approval',
  'player_to_scout': 'admin_approval',
  'player_to_scouter': 'admin_approval',
  'player_to_referee': 'admin_approval',
  'player_to_medical': 'admin_approval',
  'venue_owner_to_organizer': 'admin_approval'
};

export const getUserRoles = async (userId: string) => {
  const { data, error } = await supabase
    .from('user_roles')
    .select('role, status, is_primary_role, last_used_at')
    .eq('user_id', userId);

  if (error) throw error;
  return data;
};

export const checkRoleConflicts = async (userId: string, newRole: AppRole) => {
  const { data, error } = await supabase
    .rpc('check_role_conflicts', {
      _user_id: userId,
      _new_role: newRole
    });

  if (error) throw error;
  return data as Array<{ conflicting_role: AppRole; reason: string }>;
};

export const requestRole = async (
  roleType: AppRole,
  note: string,
  roleData?: any,
  documents?: any
) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Check for conflicts
  const conflicts = await checkRoleConflicts(user.id, roleType);
  if (conflicts && conflicts.length > 0) {
    throw new Error(`Cannot request ${roleType}: conflicts with ${conflicts[0].conflicting_role}`);
  }

  // Create request
  const { error } = await supabase
    .from('role_requests')
    .insert({
      user_id: user.id,
      requested_role: roleType,
      note,
      role_specific_data: roleData,
      documents
    });

  if (error) throw error;

  return { success: true };
};

export const getAvailableRoles = (currentRoles: AppRole[]): AppRole[] => {
  const allRoles: AppRole[] = ['parent', 'player', 'coach', 'club_owner'];
  // const allRoles: AppRole[] = ['parent', 'player', 'coach', 'club_owner', 'scout', 'referee', 'medical_staff', 'venue_owner', 'tournament_organizer'];
  return allRoles.filter(role => !currentRoles.includes(role));
};

export const getPendingRequests = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('role_requests')
    .select('*')
    .eq('user_id', user.id)
    .order('requested_at', { ascending: false });

  if (error) throw error;
  return data;
};

export const getAllPendingRequests = async () => {
  const { data, error } = await supabase
    .from('role_requests')
    .select(`
      *,
      profiles!user_id (display_name, avatar_url, is_verified)
    `)
    .eq('status', 'pending')
    .order('requested_at', { ascending: false });

  if (error) throw error;

  // Fetch current roles for each user from user_roles
  if (data) {
    const enrichedData = await Promise.all(
      data.map(async (request) => {
        const { data: userRoles } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', request.user_id)
          .eq('status', 'active');

        return {
          ...request,
          current_roles: userRoles?.map(ur => ur.role) || []
        };
      })
    );
    return enrichedData;
  }

  return data;
};

export const approveRoleRequest = async (requestId: string, requestUserId: string, approvedRole: AppRole, comment?: string) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Update the role_requests table with approval status and comment
  const { error: updateError } = await supabase
    .from('role_requests')
    .update({
      status: 'approved',
      reviewed_by: user.id,
      reviewed_at: new Date().toISOString(),
      comment: comment || null
    })
    .eq('id', requestId);

  if (updateError) throw updateError;

  return { success: true };
};

export const rejectRoleRequest = async (requestId: string, comment?: string) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('role_requests')
    .update({
      status: 'rejected',
      reviewed_by: user.id,
      reviewed_at: new Date().toISOString(),
      comment: comment || null
    })
    .eq('id', requestId);

  if (error) throw error;
  return { success: true };
};

export const getRoleRoutePrefix = (role: AppRole): string => {
  switch (role) {
    case 'super_admin':
      return 'super';
    case 'admin':
      return 'admin';
    case 'club_owner':
      return 'owner';
    case 'tournament_organizer':
      return 'organizer';
    case 'team_manager':
      return 'media-manager';
    case 'medical_staff':
      return 'medical';
    case 'venue_owner':
      return 'venue';
    default:
      return role;
  }
};
